import { useEffect, useState, useCallback, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { EncabezadoDeGestion } from '@/features/cotiza/components/encabezado-de-gestion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Search,
  ContactRound,
  ChevronDown,
  ChevronUp,
  UserPlus,
  Mail,
  Phone,
  Star,
  Building2,
  Globe,
  AlertTriangle,
  Upload,
} from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { useCustomerStore } from '../store/customer-store';
import { customerService } from '../services/customer-service';
import { MasterDataImportDialog } from '@/features/master-data/components/import-csv-dialog';
import { HubspotCompanyDetailDrawer } from '../components/hubspot-company-detail-drawer';
import {
  Customer,
  CustomerSegment,
  CreateCustomerData,
  UpdateCustomerData,
  CreateContactData,
  UpdateContactData,
  CustomerContact,
  UnifiedCustomer,
} from '../types/customer.types';

// ========================================
// Constantes
// ========================================

const SEGMENTS: CustomerSegment[] = ['ENTERPRISE', 'SMB', 'GOBIERNO', 'STARTUP'];

const SEGMENT_LABELS: Record<CustomerSegment, string> = {
  ENTERPRISE: 'Enterprise',
  SMB: 'PyME',
  GOBIERNO: 'Gobierno',
  STARTUP: 'Startup',
};

const SEGMENT_VARIANTS: Record<CustomerSegment, 'default' | 'secondary' | 'info' | 'warning'> = {
  ENTERPRISE: 'default',
  SMB: 'secondary',
  GOBIERNO: 'info',
  STARTUP: 'warning',
};

interface CustomerFormData {
  name: string;
  code: string;
  taxId: string;
  email: string;
  phone: string;
  address: string;
  industry: string;
  segment: CustomerSegment;
  website: string;
  notes: string;
}

const INITIAL_CUSTOMER_FORM: CustomerFormData = {
  name: '',
  code: '',
  taxId: '',
  email: '',
  phone: '',
  address: '',
  industry: '',
  segment: 'SMB',
  website: '',
  notes: '',
};

interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position: string;
  isPrimary: boolean;
}

const INITIAL_CONTACT_FORM: ContactFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  position: '',
  isPrimary: false,
};

// ========================================
// Componente principal
// ========================================

export function CustomersPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const {
    customers,
    fetchCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    addContact,
    updateContact,
    deleteContact,
    unifiedCustomers,
    unifiedTotal,
    unifiedPage,
    unifiedPageSize,
    sourceFilter,
    unifiedLoading,
    fetchUnified,
    setSourceFilter,
    setUnifiedSearch,
    setUnifiedPage,
  } = useCustomerStore();

  // Estado general
  const [searchQuery] = useState('');
  const [segmentFilter] = useState<string>('ALL');
  const [expandedCustomerId, setExpandedCustomerId] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);

  // Drawer HubSpot
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerCompany, setDrawerCompany] = useState<UnifiedCustomer | null>(null);

  // Búsqueda unificada con debounce
  const [searchInput, setSearchInput] = useState('');

  // Dialogs de cliente
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [deletingCustomer, setDeletingCustomer] = useState<Customer | null>(null);
  const [customerForm, setCustomerForm] = useState<CustomerFormData>(INITIAL_CUSTOMER_FORM);

  // Dialogs de contacto
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isDeleteContactDialogOpen, setIsDeleteContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<CustomerContact | null>(null);
  const [deletingContact, setDeletingContact] = useState<{
    contact: CustomerContact;
    customerId: string;
  } | null>(null);
  const [contactCustomerId, setContactCustomerId] = useState<string | null>(null);
  const [contactForm, setContactForm] = useState<ContactFormData>(INITIAL_CONTACT_FORM);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // ========================================
  // Efectos
  // ========================================

  const loadCustomers = useCallback(() => {
    const search = searchQuery.trim() || undefined;
    const segment = segmentFilter !== 'ALL' ? (segmentFilter as CustomerSegment) : undefined;
    fetchCustomers(search, segment).catch(() => {});
  }, [fetchCustomers, searchQuery, segmentFilter]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // Carga inicial de la lista unificada
  useEffect(() => {
    fetchUnified();
  }, [fetchUnified]);

  // Debounce para búsqueda unificada
  useEffect(() => {
    const t = setTimeout(() => setUnifiedSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput, setUnifiedSearch]);

  // ========================================
  // Handlers de cliente
  // ========================================

  const handleOpenCreate = () => {
    setCustomerForm(INITIAL_CUSTOMER_FORM);
    setIsCreateDialogOpen(true);
  };

  const handleOpenEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setCustomerForm({
      name: customer.name,
      code: customer.code,
      taxId: customer.taxId || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      industry: customer.industry || '',
      segment: customer.segment,
      website: customer.website || '',
      notes: customer.notes || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleOpenDelete = (customer: Customer) => {
    setDeletingCustomer(customer);
    setIsDeleteDialogOpen(true);
  };

  const handleCloseCreate = () => {
    setIsCreateDialogOpen(false);
    setCustomerForm(INITIAL_CUSTOMER_FORM);
  };

  const handleCloseEdit = () => {
    setIsEditDialogOpen(false);
    setEditingCustomer(null);
    setCustomerForm(INITIAL_CUSTOMER_FORM);
  };

  const handleCloseDelete = () => {
    setIsDeleteDialogOpen(false);
    setDeletingCustomer(null);
  };

  const buildCustomerPayload = (form: CustomerFormData) => ({
    name: form.name.trim(),
    code: form.code.trim(),
    taxId: form.taxId.trim() || null,
    email: form.email.trim() || null,
    phone: form.phone.trim() || null,
    address: form.address.trim() || null,
    industry: form.industry.trim() || null,
    segment: form.segment,
    website: form.website.trim() || null,
    notes: form.notes.trim() || null,
  });

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerForm.name.trim() || !customerForm.code.trim()) {
      toast({
        title: 'Error de validación',
        description: 'El nombre y código son obligatorios',
        variant: 'destructive',
      });
      return;
    }

    if (customerForm.email.trim() && !isValidEmail(customerForm.email.trim())) {
      toast({
        title: 'Error de validación',
        description: 'El email ingresado no tiene un formato válido (ej: usuario@empresa.com)',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const data: CreateCustomerData = buildCustomerPayload(customerForm);
      await createCustomer(data);
      fetchUnified();
      toast({
        title: 'Cliente creado',
        description: `El cliente ${data.name} ha sido creado correctamente`,
        variant: 'success',
      });
      handleCloseCreate();
    } catch (error: any) {
      toast({
        title: 'Error al crear cliente',
        description:
          error.response?.data?.error || error.response?.data?.message || 'Error desconocido',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCustomer) return;
    if (!customerForm.name.trim() || !customerForm.code.trim()) {
      toast({
        title: 'Error de validación',
        description: 'El nombre y código son obligatorios',
        variant: 'destructive',
      });
      return;
    }

    if (customerForm.email.trim() && !isValidEmail(customerForm.email.trim())) {
      toast({
        title: 'Error de validación',
        description: 'El email ingresado no tiene un formato válido (ej: usuario@empresa.com)',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const data: UpdateCustomerData = buildCustomerPayload(customerForm);
      await updateCustomer(editingCustomer.id, data);
      fetchUnified();
      toast({
        title: 'Cliente actualizado',
        description: `Los datos de ${data.name} han sido actualizados`,
        variant: 'success',
      });
      handleCloseEdit();
    } catch (error: any) {
      toast({
        title: 'Error al actualizar cliente',
        description:
          error.response?.data?.error || error.response?.data?.message || 'Error desconocido',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingCustomer) return;
    setIsSubmitting(true);
    try {
      await deleteCustomer(deletingCustomer.id);
      fetchUnified();
      toast({
        title: 'Cliente eliminado',
        description: `${deletingCustomer.name} ha sido eliminado`,
        variant: 'success',
      });
      handleCloseDelete();
    } catch (error: any) {
      toast({
        title: 'Error al eliminar cliente',
        description:
          error.response?.data?.error || error.response?.data?.message || 'Error desconocido',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========================================
  // Handlers de contacto
  // ========================================

  const handleOpenAddContact = (customerId: string) => {
    setContactCustomerId(customerId);
    setEditingContact(null);
    setContactForm(INITIAL_CONTACT_FORM);
    setIsContactDialogOpen(true);
  };

  const handleOpenEditContact = (customerId: string, contact: CustomerContact) => {
    setContactCustomerId(customerId);
    setEditingContact(contact);
    setContactForm({
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phone: contact.phone || '',
      position: contact.position || '',
      isPrimary: contact.isPrimary,
    });
    setIsContactDialogOpen(true);
  };

  const handleOpenDeleteContact = (customerId: string, contact: CustomerContact) => {
    setDeletingContact({ contact, customerId });
    setIsDeleteContactDialogOpen(true);
  };

  const handleCloseContactDialog = () => {
    setIsContactDialogOpen(false);
    setEditingContact(null);
    setContactCustomerId(null);
    setContactForm(INITIAL_CONTACT_FORM);
  };

  const handleCloseDeleteContact = () => {
    setIsDeleteContactDialogOpen(false);
    setDeletingContact(null);
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactCustomerId) return;
    if (
      !contactForm.firstName.trim() ||
      !contactForm.lastName.trim() ||
      !contactForm.email.trim()
    ) {
      toast({
        title: 'Error de validación',
        description: 'Nombre, apellido y email son obligatorios',
        variant: 'destructive',
      });
      return;
    }

    if (!isValidEmail(contactForm.email.trim())) {
      toast({
        title: 'Error de validación',
        description: 'El email del contacto no tiene un formato válido (ej: usuario@empresa.com)',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: CreateContactData | UpdateContactData = {
        firstName: contactForm.firstName.trim(),
        lastName: contactForm.lastName.trim(),
        email: contactForm.email.trim(),
        phone: contactForm.phone.trim() || null,
        position: contactForm.position.trim() || null,
        isPrimary: contactForm.isPrimary,
      };

      if (editingContact) {
        await updateContact(contactCustomerId, editingContact.id, payload as UpdateContactData);
        toast({
          title: 'Contacto actualizado',
          description: `${payload.firstName} ${payload.lastName} ha sido actualizado`,
          variant: 'success',
        });
      } else {
        await addContact(contactCustomerId, payload as CreateContactData);
        toast({
          title: 'Contacto agregado',
          description: `${payload.firstName} ${payload.lastName} ha sido agregado`,
          variant: 'success',
        });
      }
      handleCloseContactDialog();
    } catch (error: any) {
      toast({
        title: editingContact ? 'Error al actualizar contacto' : 'Error al agregar contacto',
        description:
          error.response?.data?.error || error.response?.data?.message || 'Error desconocido',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteContactConfirm = async () => {
    if (!deletingContact) return;
    setIsSubmitting(true);
    try {
      await deleteContact(deletingContact.customerId, deletingContact.contact.id);
      toast({
        title: 'Contacto eliminado',
        description: `${deletingContact.contact.firstName} ${deletingContact.contact.lastName} ha sido eliminado`,
        variant: 'success',
      });
      handleCloseDeleteContact();
    } catch (error: any) {
      toast({
        title: 'Error al eliminar contacto',
        description:
          error.response?.data?.error || error.response?.data?.message || 'Error desconocido',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========================================
  // Toggle expandir fila
  // ========================================

  const toggleExpanded = (customerId: string) => {
    setExpandedCustomerId((prev) => (prev === customerId ? null : customerId));
  };

  const handleRowClick = (row: UnifiedCustomer) => {
    if (row.source === 'PRIXIA') {
      navigate(`/customers/${row.id}`);
    } else {
      setDrawerCompany(row);
      setDrawerOpen(true);
    }
  };

  // ========================================
  // Render helpers
  // ========================================

  const renderTableSkeleton = () => (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-40" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-24" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-36" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-28" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-12" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-16" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-8 w-24" />
          </TableCell>
        </TableRow>
      ))}
    </>
  );

  const renderContactsRow = (customer: Customer) => {
    const contacts = customer.contacts || [];
    return (
      <TableRow className="bg-muted/30">
        <TableCell colSpan={7} className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold">Contactos ({contacts.length})</h4>
              <Button size="sm" variant="outline" onClick={() => handleOpenAddContact(customer.id)}>
                <UserPlus className="w-4 h-4 mr-1" />
                Agregar Contacto
              </Button>
            </div>
            {contacts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No hay contactos registrados</p>
            ) : (
              <div className="grid gap-2">
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="flex items-center justify-between bg-background rounded-lg border p-3"
                  >
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {contact.firstName} {contact.lastName}
                          </span>
                          {contact.isPrimary && (
                            <Badge variant="warning" className="text-xs">
                              <Star className="w-3 h-3 mr-1" />
                              Principal
                            </Badge>
                          )}
                        </div>
                        {contact.position && (
                          <p className="text-xs text-muted-foreground">{contact.position}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {contact.email}
                        </span>
                        {contact.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {contact.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleOpenEditContact(customer.id, contact)}
                        title="Editar contacto"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleOpenDeleteContact(customer.id, contact)}
                        title="Eliminar contacto"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const renderCustomerFormFields = () => (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="customer-name">Nombre *</Label>
          <Input
            id="customer-name"
            placeholder="Nombre del cliente"
            value={customerForm.name}
            onChange={(e) => {
              const val = e.target.value;
              if (/^[a-zA-ZÀ-ÿñÑ\s.\-']*$/.test(val)) {
                setCustomerForm((prev) => ({ ...prev, name: val }));
              }
            }}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="customer-code">Código *</Label>
          <Input
            id="customer-code"
            placeholder="CLI-001"
            value={customerForm.code}
            onChange={(e) => setCustomerForm((prev) => ({ ...prev, code: e.target.value }))}
            required
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="customer-segment">Segmento</Label>
          <Select
            value={customerForm.segment}
            onValueChange={(value) =>
              setCustomerForm((prev) => ({ ...prev, segment: value as CustomerSegment }))
            }
          >
            <SelectTrigger id="customer-segment">
              <SelectValue placeholder="Seleccionar segmento" />
            </SelectTrigger>
            <SelectContent>
              {SEGMENTS.map((seg) => (
                <SelectItem key={seg} value={seg}>
                  {SEGMENT_LABELS[seg]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="customer-industry">Industria</Label>
          <Input
            id="customer-industry"
            placeholder="Ej: Tecnologia, Banca, Salud..."
            value={customerForm.industry}
            onChange={(e) => setCustomerForm((prev) => ({ ...prev, industry: e.target.value }))}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="customer-email">Email</Label>
          <Input
            id="customer-email"
            type="email"
            placeholder="contacto@empresa.com"
            value={customerForm.email}
            onChange={(e) => setCustomerForm((prev) => ({ ...prev, email: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="customer-phone">Teléfono</Label>
          <Input
            id="customer-phone"
            placeholder="+591 7XXXXXXX"
            value={customerForm.phone}
            onChange={(e) => {
              const val = e.target.value;
              if (/^[+\d\s\-()]*$/.test(val)) {
                setCustomerForm((prev) => ({ ...prev, phone: val }));
              }
            }}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="customer-taxId">NIT / Tax ID</Label>
          <Input
            id="customer-taxId"
            placeholder="Número de identificación fiscal"
            value={customerForm.taxId}
            onChange={(e) => setCustomerForm((prev) => ({ ...prev, taxId: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="customer-website">Sitio Web</Label>
          <Input
            id="customer-website"
            placeholder="https://www.ejemplo.com"
            value={customerForm.website}
            onChange={(e) => setCustomerForm((prev) => ({ ...prev, website: e.target.value }))}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="customer-address">Dirección</Label>
        <Input
          id="customer-address"
          placeholder="Dirección completa"
          value={customerForm.address}
          onChange={(e) => setCustomerForm((prev) => ({ ...prev, address: e.target.value }))}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="customer-notes">Notas</Label>
        <Textarea
          id="customer-notes"
          placeholder="Notas adicionales sobre el cliente..."
          value={customerForm.notes}
          onChange={(e) => setCustomerForm((prev) => ({ ...prev, notes: e.target.value }))}
          rows={3}
        />
      </div>
    </>
  );

  return (
    <AppLayout>
      <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-8">
        <div className="space-y-6">
          {/* El encabezado de Gestión (artefacto, 19-oct): el paso «Clientes». */}
          <EncabezadoDeGestion
            activa="clientes"
            dataTour="customers-header"
            titulo="Clientes"
            subtitulo="Con quién trabajás: contactos e historial de cotizaciones."
            acciones={
              <>
                <Button variant="outline" onClick={() => setShowImport(true)}>
                  <Upload className="h-4 w-4" aria-hidden="true" />
                  Importar CSV
                </Button>
                <Button
                  variant="degradado"
                  size="lg"
                  className="px-5"
                  data-tour="customers-create-btn"
                  onClick={handleOpenCreate}
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Nuevo cliente
                </Button>
              </>
            }
          />

          {/* Filtros */}
          <div data-tour="customers-filters" className="space-y-3">
            {/* Barra de búsqueda */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre, código, NIT o email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            {/* Filtro por origen */}
            <div className="flex items-center gap-2">
              <Button
                variant={sourceFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSourceFilter('all')}
              >
                Todos
              </Button>
              <Button
                variant={sourceFilter === 'prixia' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSourceFilter('prixia')}
              >
                Prixia
              </Button>
              <Button
                variant={sourceFilter === 'hubspot' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSourceFilter('hubspot')}
              >
                HubSpot
              </Button>
              <span className="ml-auto text-sm text-muted-foreground">
                {unifiedTotal} resultados
              </span>
            </div>
          </div>

          {/* Tabla de clientes */}
          <Card data-tour="customers-table">
            <CardHeader>
              <CardTitle>Lista de Clientes</CardTitle>
              <CardDescription>
                {unifiedTotal} cliente{unifiedTotal !== 1 ? 's' : ''} encontrado
                {unifiedTotal !== 1 ? 's' : ''}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Código</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Segmento</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead className="text-center">Cotizaciones</TableHead>
                    <TableHead>Origen</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unifiedLoading ? (
                    renderTableSkeleton()
                  ) : unifiedCustomers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                        <div className="flex flex-col items-center gap-2">
                          <ContactRound className="h-10 w-10 text-muted-foreground/50" />
                          {sourceFilter === 'hubspot' ? (
                            <span>
                              No hay empresas de HubSpot.{' '}
                              <a href="/integrations" className="text-primary underline">
                                Configurá la integración →
                              </a>
                            </span>
                          ) : searchInput || sourceFilter !== 'all' ? (
                            'No se encontraron clientes con esos criterios de búsqueda'
                          ) : (
                            'No hay clientes registrados. Crea el primero con el botón "Nuevo Cliente".'
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    unifiedCustomers.map((row) => {
                      const isPrixia = row.source === 'PRIXIA';
                      // Para clientes Prixia buscamos el objeto Customer completo en la lista local
                      const customer = isPrixia ? customers.find((c) => c.id === row.id) : null;
                      const isExpanded = expandedCustomerId === row.id;
                      return (
                        <Fragment key={`${row.source}-${row.id}`}>
                          <TableRow
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => handleRowClick(row)}
                          >
                            <TableCell className="font-mono text-sm">{row.code ?? '-'}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{row.name}</span>
                                {row.industry && (
                                  <span className="text-xs text-muted-foreground">
                                    <Building2 className="w-3 h-3 inline mr-0.5" />
                                    {row.industry}
                                  </span>
                                )}
                                {row.website && <Globe className="w-3 h-3 text-muted-foreground" />}
                              </div>
                            </TableCell>
                            <TableCell>
                              {row.segment && SEGMENT_VARIANTS[row.segment as CustomerSegment] ? (
                                <Badge variant={SEGMENT_VARIANTS[row.segment as CustomerSegment]}>
                                  {SEGMENT_LABELS[row.segment as CustomerSegment]}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-sm">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {row.email || '-'}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {row.phone || '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline">
                                {customer?._count?.quotes ?? (isPrixia ? 0 : '-')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  row.source === 'HUBSPOT'
                                    ? 'bg-alerta-suave text-alerta border-alerta-borde'
                                    : 'bg-muted text-muted-foreground border-border'
                                }
                              >
                                {row.source === 'HUBSPOT' ? 'HubSpot' : 'Prixia'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div
                                className="flex items-center justify-end gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {isPrixia && customer && (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={() => handleOpenEdit(customer)}
                                      title="Editar cliente"
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={() => handleOpenDelete(customer)}
                                      title="Eliminar cliente"
                                    >
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      onClick={() => toggleExpanded(row.id)}
                                      title={isExpanded ? 'Ocultar contactos' : 'Ver contactos'}
                                    >
                                      {isExpanded ? (
                                        <ChevronUp className="h-4 w-4" />
                                      ) : (
                                        <ChevronDown className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                          {isExpanded && customer && renderContactsRow(customer)}
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>

              {/* Paginación */}
              <div className="flex items-center justify-between mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={unifiedPage <= 1 || unifiedLoading}
                  onClick={() => setUnifiedPage(unifiedPage - 1)}
                >
                  Anterior
                </Button>
                <span className="text-sm text-muted-foreground">
                  Página {unifiedPage} de {Math.max(1, Math.ceil(unifiedTotal / unifiedPageSize))}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={unifiedPage * unifiedPageSize >= unifiedTotal || unifiedLoading}
                  onClick={() => setUnifiedPage(unifiedPage + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ======================================== */}
      {/* Dialog: Crear Cliente */}
      {/* ======================================== */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo Cliente</DialogTitle>
            <DialogDescription>
              Completa los datos para registrar un nuevo cliente
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateSubmit} className="space-y-4">
            {renderCustomerFormFields()}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseCreate}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Crear Cliente
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ======================================== */}
      {/* Dialog: Editar Cliente */}
      {/* ======================================== */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
            <DialogDescription>Actualiza los datos del cliente seleccionado</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            {renderCustomerFormFields()}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseEdit}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Guardar Cambios
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ======================================== */}
      {/* Dialog: Confirmar Eliminar Cliente */}
      {/* ======================================== */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Eliminar Cliente
            </DialogTitle>
            <DialogDescription>
              Esta accion no se puede deshacer. Se eliminara el cliente{' '}
              <strong>{deletingCustomer?.name}</strong> y todos sus datos asociados.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={handleCloseDelete} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ======================================== */}
      {/* Dialog: Agregar/Editar Contacto */}
      {/* ======================================== */}
      <Dialog open={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Editar Contacto' : 'Agregar Contacto'}</DialogTitle>
            <DialogDescription>
              {editingContact
                ? 'Actualiza los datos del contacto'
                : 'Completa los datos del nuevo contacto'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleContactSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact-firstName">Nombre *</Label>
                <Input
                  id="contact-firstName"
                  placeholder="Nombre"
                  value={contactForm.firstName}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^[a-zA-ZÀ-ÿñÑ\s.\-']*$/.test(val)) {
                      setContactForm((prev) => ({ ...prev, firstName: val }));
                    }
                  }}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-lastName">Apellido *</Label>
                <Input
                  id="contact-lastName"
                  placeholder="Apellido"
                  value={contactForm.lastName}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^[a-zA-ZÀ-ÿñÑ\s.\-']*$/.test(val)) {
                      setContactForm((prev) => ({ ...prev, lastName: val }));
                    }
                  }}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-email">Email *</Label>
              <Input
                id="contact-email"
                type="email"
                placeholder="contacto@empresa.com"
                value={contactForm.email}
                onChange={(e) => setContactForm((prev) => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact-phone">Teléfono</Label>
                <Input
                  id="contact-phone"
                  placeholder="+591 7XXXXXXX"
                  value={contactForm.phone}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^[+\d\s\-()]*$/.test(val)) {
                      setContactForm((prev) => ({ ...prev, phone: val }));
                    }
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-position">Cargo</Label>
                <Input
                  id="contact-position"
                  placeholder="Ej: Gerente de TI"
                  value={contactForm.position}
                  onChange={(e) =>
                    setContactForm((prev) => ({ ...prev, position: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="contact-isPrimary"
                checked={contactForm.isPrimary}
                onCheckedChange={(checked) =>
                  setContactForm((prev) => ({ ...prev, isPrimary: checked === true }))
                }
              />
              <Label htmlFor="contact-isPrimary" className="text-sm font-normal cursor-pointer">
                Contacto principal
              </Label>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseContactDialog}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingContact ? 'Guardar Cambios' : 'Agregar Contacto'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ======================================== */}
      {/* Dialog: Importar CSV */}
      {/* ======================================== */}
      <MasterDataImportDialog
        open={showImport}
        onOpenChange={setShowImport}
        entityName="Clientes"
        templateHeaders={[
          'code',
          'name',
          'segment',
          'email',
          'phone',
          'taxId',
          'address',
          'industry',
          'website',
          'notes',
        ]}
        templateRows={[
          [
            'CLI-001',
            'Empresa Ejemplo S.A.',
            'ENTERPRISE',
            'contacto@empresa.com',
            '+591 77777777',
            '123456789',
            'Av. Principal 100',
            'Tecnologia',
            'https://empresa.com',
            'Cliente prioritario',
          ],
          [
            'CLI-002',
            'PyME Bolivia',
            'SMB',
            'info@pymebolivia.com',
            '+591 22222222',
            '',
            'Calle Comercio 50',
            'Retail',
            '',
            '',
          ],
          [
            'CLI-003',
            'Gobierno Municipal',
            'GOBIERNO',
            'compras@municipio.gob',
            '',
            '987654321',
            'Plaza Central s/n',
            'Gobierno',
            '',
            'Licitación publica',
          ],
        ]}
        importFn={(file) => customerService.importCustomers(file)}
        onSuccess={() => {
          loadCustomers();
          fetchUnified();
        }}
      />

      {/* ======================================== */}
      {/* Drawer: Detalle empresa HubSpot */}
      {/* ======================================== */}
      <HubspotCompanyDetailDrawer
        open={drawerOpen}
        onOpenChange={(open) => {
          setDrawerOpen(open);
          if (!open) setDrawerCompany(null);
        }}
        company={drawerCompany}
      />

      {/* ======================================== */}
      {/* Dialog: Confirmar Eliminar Contacto */}
      {/* ======================================== */}
      <Dialog open={isDeleteContactDialogOpen} onOpenChange={setIsDeleteContactDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Eliminar Contacto
            </DialogTitle>
            <DialogDescription>
              Se eliminara el contacto{' '}
              <strong>
                {deletingContact?.contact.firstName} {deletingContact?.contact.lastName}
              </strong>
              . Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={handleCloseDeleteContact} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteContactConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

export default CustomersPage;
